import React from 'react';

import { IStore } from 'features/store/pages/pickup-store-selector/ducks/stores';
import { StyleSheet } from 'react-native';
import { SceneRendererProps } from 'react-native-tab-view';

import { IRestaurant } from '@rbi-ctg/store';
import { TabView } from 'components/tab-view';
import { ITab } from 'components/tab-view/types';
import { Box } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { layout, primitive } from 'styles/constants';

import StoreCardList from '../store-card-list';
import { OnRestaurantHighlight, StoreCardListRef, StoreLocatorActions } from '../types';

const intlIdMap: { [K in OrderLocatorTab]: TranslationKey } = {
  [OrderLocatorTab.NEARBY]: 'nearby',
  [OrderLocatorTab.RECENT]: 'recents',
  [OrderLocatorTab.FAVORITE]: 'favorite',
  [OrderLocatorTab.DELIVERY]: 'delivery',
};

interface IListViewProps {
  onTabChange: (tab: OrderLocatorTab) => void;
  onSelectStore?: (storeId: string) => void;
  onRestaurantHighlight: OnRestaurantHighlight;
  onFavoriteClick: () => void;
  currentTabKey: OrderLocatorTab;
  currentTabIndex: number;
  currentTabHasError: boolean;
  locatorTabs: OrderLocatorTab[];
  isInSupportForm?: boolean;
  nearbyOnly?: boolean;
  /** Radio list + sticky “Select this Restaurant” footer (minimal / offers picker). */
  isOffersSelection?: boolean;
  restaurantFocused: IStore | null;
  storesByTab: {
    [K in OrderLocatorTab]: IStore[];
  };
  flatListRef: React.RefObject<StoreCardListRef | null>;
  storesById: Record<string, IRestaurant>;
  actions: React.MutableRefObject<StoreLocatorActions>;
  tabSearchStatusMap: {
    [K in OrderLocatorTab]: boolean;
  };
  isStoreEligible?: (store: IStore) => boolean;
  /** Current selected restaurant — pinned above eligible nearby for offers picker. */
  activeStore?: IStore | null;
  /** Banner when a picker store is ineligible (menu Tap-here). */
  ineligibleStoreMessage?: string;
  /** List empty state after an offer restriction removes every nearby restaurant. */
  noEligibleStoresMessage?: string;
}

export const ListView: React.FC<IListViewProps> = ({
  onSelectStore,
  onRestaurantHighlight,
  onTabChange,
  isInSupportForm,
  nearbyOnly,
  isOffersSelection,
  restaurantFocused,
  currentTabKey,
  currentTabHasError,
  locatorTabs,
  storesByTab,
  tabSearchStatusMap,
  flatListRef,
  storesById,
  onFavoriteClick,
  actions,
  isStoreEligible,
  activeStore,
  ineligibleStoreMessage,
  noEligibleStoresMessage,
}) => {
  const isDesktop = useIsDesktopBreakpoint();

  // Minimal / offers picker: nearby-only list (no Recents/Favorites).
  const visibleTabs = nearbyOnly || isOffersSelection ? [OrderLocatorTab.NEARBY] : locatorTabs;

  const tabs = visibleTabs.map(key => ({
    key,
    title: t(intlIdMap[key]),
  }));

  const activeTabIndex =
    nearbyOnly || isOffersSelection ? 0 : Math.max(0, visibleTabs.indexOf(currentTabKey));

  // As we need to pass additional props, we have to use a custom renderScene function instead of SceneMap
  // Do not return inline functions because  If you pass inline functions, it'll re-create the component every render, which will cause the entire route to unmount and remount every change. It's very bad for performance and will also cause any local state to be lost.
  const renderScene = ({ route }: SceneRendererProps & { route: ITab }) => {
    const tabKey = route.key as OrderLocatorTab;
    // Setting display none for desktop we ensure the non current tabs will be completeley disable in terms of accessibility.
    // With this hack we ensure the user will be able to properly navigate the desktop site using the keyboard.
    const desktopDisplay =
      isDesktop && tabKey !== currentTabKey && !(nearbyOnly || isOffersSelection) ? 'none' : 'flex';

    return (
      <Box style={[styles.storeCardListContainer, desktopDisplay === 'none' && styles.hidden]}>
        <StoreCardList
          tab={tabKey}
          currentTabHasError={currentTabHasError}
          restaurants={storesByTab[tabKey]}
          isSearching={tabSearchStatusMap[tabKey]}
          isInSupportForm={isInSupportForm}
          isOffersSelection={isOffersSelection}
          restaurantFocused={restaurantFocused}
          onSelectStore={onSelectStore}
          onRestaurantHighlight={onRestaurantHighlight}
          onFavoriteClick={onFavoriteClick}
          // Only pass the ref to the current tab
          ref={tabKey === currentTabKey || nearbyOnly || isOffersSelection ? flatListRef : null}
          storesById={storesById}
          actions={actions}
          isStoreEligible={isStoreEligible}
          activeStore={activeStore}
          ineligibleStoreMessage={ineligibleStoreMessage}
          noEligibleStoresMessage={noEligibleStoresMessage}
        />
      </Box>
    );
  };

  return (
    <TabView
      activeTabIndex={activeTabIndex}
      handleTabIndexChange={(index: number) => onTabChange(visibleTabs[index])}
      tabs={tabs}
      renderScene={renderScene}
      backgroundColor={primitive.$white}
      hideTabBar={!!(nearbyOnly || isOffersSelection)}
      style={styles.tabView}
    />
  );
};

const styles = StyleSheet.create({
  hidden: {
    display: 'none',
  },
  storeCardListContainer: {
    backgroundColor: primitive.$white,
    flex: 1,
    width: layout.full,
  },
  tabView: {
    flex: 1,
  },
});
